// Safety patterns — any path segment matching these is treated as a sensitive
// dynamic param and never rendered as visible chrome text. Add new shapes
// here when introducing routes with new param formats (slugs that embed PII,
// etc.).
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_ID_PATTERN = /^\d+$/;
const EMAIL_PATTERN = /@/;

function looksLikeDynamicParam(segment: string): boolean {
  return (
    UUID_PATTERN.test(segment) || NUMERIC_ID_PATTERN.test(segment) || EMAIL_PATTERN.test(segment)
  );
}

export type RouteOverride = { label: string; href: string };

export type Breadcrumb = { label: string; href: string; isLast: boolean };

function resolveSegmentLabel(
  segment: string,
  overrides: Record<string, RouteOverride>,
  t: (key: string) => string,
): string {
  if (overrides[segment]) return overrides[segment].label;
  if (looksLikeDynamicParam(segment)) return t('common.detail');
  return segment.charAt(0).toUpperCase() + segment.slice(1).replaceAll('-', ' ');
}

export function buildBreadcrumbs(
  pathname: string,
  overrides: Record<string, RouteOverride>,
  t: (key: string) => string,
): Breadcrumb[] {
  const segments = (pathname || '/').split('/').filter((s) => s.length > 0);
  const nonDashboardSegments = segments.filter((s) => s !== 'dashboard');

  const segmentCrumbs = nonDashboardSegments.reduce<{ crumbs: Breadcrumb[]; prevHref: string }>(
    (acc, segment, index) => {
      const override = overrides[segment];
      const href = override
        ? override.href
        : acc.prevHref
          ? `${acc.prevHref}/${segment}`
          : `/dashboard/${nonDashboardSegments.slice(0, index + 1).join('/')}`;

      acc.crumbs.push({
        label: resolveSegmentLabel(segment, overrides, t),
        href,
        isLast: index === nonDashboardSegments.length - 1,
      });

      return { crumbs: acc.crumbs, prevHref: href };
    },
    { crumbs: [], prevHref: '' },
  );

  return [
    { label: t('layout.dashboard'), href: '/dashboard', isLast: nonDashboardSegments.length === 0 },
    ...segmentCrumbs.crumbs,
  ];
}

export function resolvePageTitle(
  pathname: string,
  overrides: Record<string, RouteOverride>,
  t: (key: string) => string,
): string {
  const segments = (pathname || '/').split('/').filter((s) => s.length > 0);
  const lastSegment = segments.at(-1);
  return lastSegment ? resolveSegmentLabel(lastSegment, overrides, t) : t('layout.dashboard');
}
